import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendUrl';
import { formatCurrency } from '../utils/currency';

const CuratedOptions = ({ theme, eventData, onAddItem, onBrowseCatalog, compact = false }) => {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const country = eventData?.country || 'US';
  const formatCatalogPrice = (value) => {
    const amount = Number(value || 0);
    const decimals = Math.abs(amount % 1) > 0.000001 ? 2 : 0;
    return formatCurrency(amount, country, { decimals });
  };
  const formatEstimatedCatalogPrice = (value) => `Est. ${formatCatalogPrice(value)}`;

  useEffect(() => {
    const fetchOptions = async () => {
      if (!theme || !theme.name) return;
      setLoading(true);
      try {
        const payload = {
          themeName: theme.name,
          palette: theme.color_palette || [],
          budget: eventData?.budget || null,
          guestCount: eventData?.guest_count || null,
          eventType: eventData?.event_type || null,
          ageRange: eventData?.age_range || null,
          venueType: eventData?.venue_type || null,
          venue: eventData?.venue || null,
          location: eventData?.location || null,
          meals: eventData?.meals || [],
          additionalDetails: eventData?.additional_details || null,
          categories: ['Decor', 'Tableware', 'Party Supplies', 'Rentals', 'Activities', 'Printables']
        };
        const res = await axios.post(`${BACKEND_URL}/api/catalog/items/match`, payload);
        // Expecting { "Tableware": [item1, item2], "Decor": [item3] }
        setOptions(res.data || {});
      } catch (err) {
        console.error("Failed to fetch curated options", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [theme, eventData]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white px-6 py-8 text-center text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
        Matching catalog options to {theme?.name}...
      </div>
    );
  }

  if (Object.keys(options).length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/70 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600">Catalog matching</p>
            <h3 className="mt-2 text-xl font-bold text-gray-950">Browse supplies, decor, and activity ideas</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
              No automatic matches surfaced yet, but the catalog explorer can still help you add reusable decor, party supplies, rentals, printables, and activities to this plan.
            </p>
          </div>
          {onBrowseCatalog && (
            <button
              onClick={onBrowseCatalog}
              className="h-11 rounded-xl bg-gray-950 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-gray-800"
            >
              Open catalog
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${compact ? '' : 'mb-8'} rounded-2xl border border-stone-200 bg-stone-50/70 p-5 sm:p-6`}>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600">Smart catalog matches</p>
          <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Sourcing ideas matched to this plan</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-600">
            These are catalog-backed options matched by theme language, color palette, budget, and event type. Add any option to your shopping list, then adjust quantity or price as needed.
          </p>
        </div>
        {onBrowseCatalog && (
          <button
            onClick={onBrowseCatalog}
            className="h-11 rounded-xl border border-stone-200 bg-white px-5 text-sm font-bold text-zinc-700 shadow-sm transition hover:border-stone-300 hover:bg-stone-50"
          >
            Browse catalog
          </button>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(options).map(([category, items]) => (
          <div key={category}>
            <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-700">{category}</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {items.map(item => (
                <div key={item._id} className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
                  <div className="relative h-32 overflow-hidden bg-gray-100">
                    {item.imageUrls && item.imageUrls[0] ? (
                      <img src={item.imageUrls[0]} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-gray-400">Catalog item</div>
                    )}
                    {item.minPrice && (
                      <div className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-1 text-xs font-bold text-white backdrop-blur-sm">
                        {formatEstimatedCatalogPrice(item.minPrice)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-1 flex-col p-4">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <h5 className="text-sm font-bold leading-snug text-gray-950">{item.name}</h5>
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                        {item.purchaseType === 'rent_only' ? 'Rent' : item.purchaseType === 'buy_or_rent' ? 'Buy/Rent' : 'Buy'}
                      </span>
                    </div>
                    <p className="mb-4 whitespace-normal break-words text-xs leading-relaxed text-gray-500">{item.description}</p>
                    
                    <button 
                      onClick={() => onAddItem({
                        id: `cat_${item._id || Date.now()}`,
                        category: item.category,
                        name: item.name,
                        description: item.description,
                        buy_price: item.minPrice ? formatCatalogPrice(item.minPrice) : formatCatalogPrice(0),
                        rent_price: item.purchaseType === 'rent_only' && item.minPrice ? formatCatalogPrice(item.minPrice) : null,
                        rentable: item.purchaseType !== 'buy_only',
                        purchaseType: item.purchaseType,
                        quantity: 1,
                        image_url: item.imageUrls?.[0],
                        priority: 'nice-to-have',
                        purchased: false,
                        addedAt: new Date().toISOString()
                      })}
                      className="mt-auto h-10 w-full rounded-xl bg-stone-100 text-sm font-bold text-zinc-700 transition-colors hover:bg-stone-200"
                    >
                      Add to shopping list
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CuratedOptions;
